import * as React from 'react'
import { FC, useCallback, useState } from 'react'
import ExitToAppIcon from '@mui/icons-material/ExitToApp'
import { getStyles } from './useStyles'
import { IUser } from '@user/store/authorization/typings'
import { useLogOut } from '@user/store/authorization/hooks'
import { useNavigate } from 'react-router-dom'
import Button from '@mui/material/Button'
import Avatar from '@mui/material/Avatar'
import Popover from '@mui/material/Popover'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText, { listItemTextClasses } from '@mui/material/ListItemText'
import ListItemIcon from '@mui/material/ListItemIcon'
import useTheme from '@mui/material/styles/useTheme'
import { useAppDispatch } from '@user/store/hooks'
import { GetLogoutUrl } from '@user/store/authorization'
import { unwrapResult } from '@reduxjs/toolkit'
import { updateLoadingState } from '@sales-center/common'
import { toast } from 'react-toastify'

interface Props {
  user: IUser
}

export const AvatarButton: FC<Props> = (props) => {
  const { user } = props

  const navigate = useNavigate()

  const dispatch = useAppDispatch()
  const onLogout = useLogOut()
  const onLogoutDispatch = useCallback(async () => {
    if (user.isSSOUser) {
      dispatch(updateLoadingState('visible'))
      const result = await dispatch(GetLogoutUrl())

      if (GetLogoutUrl.fulfilled.match(result)) {
        unwrapResult(result)
        const redirectUrl = result.payload

        if (redirectUrl) {
          // Now redirect to the actual SSO logout URL
          window.location.assign(redirectUrl)
        } else {
          toast.error('No logout URL received.')
        }
      } else {
        try {
          unwrapResult(result)
        } catch (e) {
          if (e instanceof Error) toast.error(e.message)
          else if (typeof e === 'string') toast.error(e)
          else toast.error('Unexpected error')
        }
      }
    } else {
      onLogout()
    }
  }, [dispatch, onLogout, user.isSSOUser])

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)
  const id = open ? 'avatar-popover' : undefined

  const onOpen = useCallback(
    (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget),
    [],
  )

  const onClose = useCallback(() => setAnchorEl(null), [])

  const onAccountSettings = useCallback(
    () => navigate('/accountSettings'),
    [navigate],
  )
  const theme = useTheme()
  const styles = getStyles(theme)
  return (
    <>
      <Button
        color="primary"
        variant={open ? 'contained' : 'outlined'}
        onClick={onOpen}
        sx={{
          ...styles.avatarButton,
          height: '47px',
          minWidth: '47px',
          padding: theme.spacing(1),
        }}
        data-test-id="profile-button"
      >
        <Avatar
          alt="No Avatar"
          src="/static-assets/images/no-avatar.svg"
          sx={{
            ...styles.avatar,
          }}
        />
        {user.firstName}
      </Button>
      <Popover
        id={id}
        open={open}
        onClose={onClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          onMouseLeave: onClose,
          elevation: 3,
          sx: styles.content,
        }}
        sx={{
          ...styles.root,
        }}
      >
        <span style={{ ...styles.header, ...styles.text }}>
          {user.userName}
        </span>

        <Divider />

        <List>
          <ListItemButton onClick={onAccountSettings}>
            <ListItemText
              sx={{ [`& .${listItemTextClasses.primary}`]: styles.text }}
              data-test-id="profile-account-settings-button"
            >
              Account Settings
            </ListItemText>
          </ListItemButton>
          <ListItemButton onClick={onLogoutDispatch}>
            <ListItemText
              sx={{ [`& .${listItemTextClasses.primary}`]: styles.text }}
              data-test-id="profile-logout-button"
            >
              Logout
            </ListItemText>
            <ListItemIcon sx={{ ...styles.listIcon }}>
              <ExitToAppIcon />
            </ListItemIcon>
          </ListItemButton>
        </List>
      </Popover>
    </>
  )
}
AvatarButton.displayName = 'AvatarButton'
